import React, { useRef, useState } from "react";
import { Typography, Button, Tooltip, message, Modal } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import api from "src/config/api";
import {
  Form,
  FormDatePicker,
  FormNumber,
  FormText,
} from "src/components/Form";
import eventBus from "src/common/eventBus";
import Table from "src/components/Table/Table";
import { formatDate } from "src/shared/dateTimeUtils";

const GeneralExpenses = () => {
  const tableRef = useRef();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);

  const showAddModal = () => {
    setIsEditMode(false);
    setCurrentExpense({
      expenseDate: new Date().toISOString(),
      category: 'General' // Set default category
    });
    setIsModalVisible(true);
  };

  const showEditModal = (expense) => {
    setIsEditMode(true);
    setCurrentExpense(expense);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (data) => {
    try {
      if (isEditMode) {
        await api.put(`expenses/${currentExpense.id}`, {
          ...data,
          category: 'General'
        });
        message.success("Expense updated successfully");
      } else {
        await api.post(`expenses`, {
          ...data,
          category: 'General'
        });
        message.success("Expense added successfully");
      }
      setIsModalVisible(false);
      tableRef.current.refresh();
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
    }
  };

  const actions = [
    <Button
      icon={<PlusOutlined />}
      onClick={showAddModal}
      type="primary"
    >
      Add Expense
    </Button>,
  ];

  const columns = [
    {
      dataIndex: "expenseDate",
      title: "Date",
      width: "120px",
      render: (date) => formatDate(date),
      defaultSortOrder: "descend",
    },
    {
      dataIndex: "expenseType",
      title: "Type",
      width: "200px",
    },
    {
      dataIndex: "description",
      title: "Description",
      width: "200px",
    },
    {
      dataIndex: "cost",
      title: "Cost",
      width: "120px",
      render: (cost) => formatCurrency(cost),
    },
    {
      dataIndex: "actions",
      title: "Actions",
      width: "120px",
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => showEditModal(row)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                eventBus.emit("dialog", {
                  type: "confirm",
                  message: `Are you sure to delete this expense?`,
                  onOk: async () => {
                    await api.delete(`expenses/${row.id}`);
                  },
                  onFinish: () => {
                    tableRef.current.refresh();
                  },
                });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Helper function to format currency in Vietnamese style
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '';
    return `${Number(amount).toLocaleString('vi-VN')} VND`;
  };

  return (
    <>
      <Typography.Title level={4} className="mb-4">
        General Expenses
      </Typography.Title>

      <Table
        ref={tableRef}
        url="expenses?category=General"
        actions={actions}
        columns={columns}
      ></Table>

      <Modal
        title={isEditMode ? "Edit Expense" : "Add Expense"}
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        destroyOnClose={true}
      >
        <Form
          onSubmit={handleSubmit}
          layout="vertical"
          defaultValues={currentExpense || {}}
        >
          <FormText
            label="Expense Type"
            placeholder="e.g., Petrol, Office Supplies, etc."
            name="expenseType"
            required
          />
          <FormText
            label="Description"
            placeholder="Additional details about the expense"
            name="description"
          />
          <FormDatePicker
            label="Date"
            placeholder="Expense Date"
            name="expenseDate"
            required
          />
          <FormNumber
            label="Cost"
            placeholder="Cost in VND"
            name="cost"
            required
          />
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginTop: "16px" }}
          >
            {isEditMode ? "Update" : "Add"}
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default GeneralExpenses; 